// useModeController — central state for the narrative / exploration /
// tour mode toggle. Persists to localStorage and writes the active mode
// to window.__planetMode so the R3F frame loop can read it without
// going through React state.
//
// The controller mirrors the subscribeScene pattern in
// useScrollTimeline: a tiny pub/sub bus plus a stable ref-like getter,
// so non-React consumers (SceneCameraRig, ColorBlockLayer, etc.) can
// pull the current value without a re-render.
//
// Three things are deliberately kept on the bus:
//   - mode changes (so the HUD can swap overlays)
//   - scene-index changes (so the controller can remember the most
//     recent scene for when the user switches back to narrative)
//   - tour-active flag (so subscribers like the analytics layer and the
//     TourOverlay can react)

import { useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PlanetMode = 'narrative' | 'exploration' | 'tour';

/** Legacy-only alias for any code that imported the old name. */
export type LegacyPlanetMode = 'narrative' | 'exploration';

export interface ModeControllerState {
  /** Current mode (cheap getter — no re-render). */
  getMode: () => PlanetMode;
  /** Current scene index (cheap getter — no re-render). */
  getSceneIndex: () => number;
  /** Subscribe to mode changes. Returns an unsubscribe fn. */
  subscribeMode: (cb: (mode: PlanetMode) => void) => () => void;
  /** Subscribe to scene-index changes (also called when in exploration). */
  subscribeScene: (cb: (idx: number) => void) => () => void;
  /** Subscribe to "tour active" flag changes. */
  subscribeTour: (cb: (active: boolean) => void) => () => void;
  /** Set the mode + persist + emit. */
  setMode: (mode: PlanetMode) => void;
  /** Cycle to the next mode: narrative → exploration → tour → narrative. */
  toggle: () => void;
  /** React-friendly hook version of the current mode. */
  useMode: () => PlanetMode;
}

declare global {
  interface Window {
    /** Current planet mode — written by setMode, read in useFrame. */
    __planetMode?: PlanetMode;
    /** Most recent scene index (remembered across mode switches). */
    __planetLastSceneIndex?: number;
    /** True when the auto-tour is actively running (not paused). */
    __planetTourActive?: boolean;
    /** Current scene index while in tour (0..6). Written by
     *  useTourController so the camera / HUD can read it without going
     *  through the bus on every frame. */
    __planetTourSceneIndex?: number;
    /** Current scene progress while in tour (0..1). */
    __planetTourProgress?: number;
    /** True when the tour is paused by a user interaction. */
    __planetTourPaused?: boolean;
  }
}

// ---------------------------------------------------------------------------
// Pub/sub
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'yhazrin:mode';

const VALID_MODES: PlanetMode[] = ['narrative', 'exploration', 'tour'];
const MODE_CYCLE: PlanetMode[] = ['narrative', 'exploration', 'tour'];

const modeListeners = new Set<(mode: PlanetMode) => void>();
const sceneListeners = new Set<(idx: number) => void>();
const tourListeners = new Set<(active: boolean) => void>();

function readStoredMode(): PlanetMode {
  if (typeof window === 'undefined') return 'narrative';
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'exploration') return 'exploration';
    if (v === 'tour') return 'tour';
    return 'narrative';
  } catch {
    return 'narrative';
  }
}

function storeMode(mode: PlanetMode) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* localStorage unavailable — best-effort, ignore */
  }
}

// ---------------------------------------------------------------------------
// Initial value — captured at module load so the first paint of the
// camera rig sees the persisted mode.
// ---------------------------------------------------------------------------

if (typeof window !== 'undefined' && !window.__planetMode) {
  window.__planetMode = readStoredMode();
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useModeController — installs the bus and exposes a small surface.
 * Mount once at the App root. Safe to mount multiple times — the bus
 * is module-singleton.
 */
export function useModeController(): ModeControllerState {
  // Mirror the current mode into React state so consumers in the HUD
  // can re-render on mode change without polling window.__planetMode.
  const [, force] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // First mount: ensure window + localStorage agree.
    window.__planetMode = readStoredMode();

    const onMode = () => force((n) => (n + 1) % 1024);
    modeListeners.add(onMode);
    return () => {
      modeListeners.delete(onMode);
    };
  }, []);

  const setMode = (mode: PlanetMode) => {
    if (typeof window === 'undefined') return;
    if (!VALID_MODES.includes(mode)) return;
    const cur = window.__planetMode ?? 'narrative';
    if (cur === mode) return;
    window.__planetMode = mode;
    storeMode(mode);
    for (const cb of modeListeners) {
      try {
        cb(mode);
      } catch {
        /* one bad listener — keep the bus alive */
      }
    }
  };

  const setSceneIndex = (idx: number) => {
    if (typeof window === 'undefined') return;
    window.__planetLastSceneIndex = idx;
    for (const cb of sceneListeners) {
      try {
        cb(idx);
      } catch {
        /* keep the bus alive */
      }
    }
  };

  /**
   * setTourActive — flips the global tour flag and emits to listeners.
   * Cheap wrapper so useTourController doesn't have to touch the
   * internal bus directly.
   */
  const setTourActive = (active: boolean) => {
    if (typeof window === 'undefined') return;
    if (!!window.__planetTourActive === active) return;
    window.__planetTourActive = active;
    for (const cb of tourListeners) {
      try {
        cb(active);
      } catch {
        /* keep the bus alive */
      }
    }
  };

  // Wrap the scene-index listener so external consumers (the
  // ColorBlockLayer, ChapterPill, etc.) keep working unchanged — we
  // just mirror their emissions through this controller's bus too.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let last = window.__planetSceneIndex ?? 0;
    const id = window.setInterval(() => {
      const cur = window.__planetSceneIndex ?? 0;
      if (cur !== last) {
        last = cur;
        setSceneIndex(cur);
      }
    }, 200);
    // Prime.
    setSceneIndex(window.__planetSceneIndex ?? 0);
    return () => window.clearInterval(id);
  }, []);

  const controller: ModeControllerState = {
    getMode: () =>
      typeof window !== 'undefined' && window.__planetMode
        ? window.__planetMode
        : 'narrative',
    getSceneIndex: () =>
      typeof window !== 'undefined' && typeof window.__planetSceneIndex === 'number'
        ? window.__planetSceneIndex
        : 0,
    subscribeMode: (cb) => {
      modeListeners.add(cb);
      return () => {
        modeListeners.delete(cb);
      };
    },
    subscribeScene: (cb) => {
      sceneListeners.add(cb);
      return () => {
        sceneListeners.delete(cb);
      };
    },
    subscribeTour: (cb) => {
      tourListeners.add(cb);
      return () => {
        tourListeners.delete(cb);
      };
    },
    setMode,
    toggle: () => {
      const cur: PlanetMode = (() => {
        const v =
          typeof window !== 'undefined' ? window.__planetMode : undefined;
        return v === 'exploration' || v === 'tour' ? v : 'narrative';
      })();
      const i = MODE_CYCLE.indexOf(cur);
      const next: PlanetMode =
        MODE_CYCLE[(i + 1) % MODE_CYCLE.length] ?? 'narrative';
      setMode(next);
    },
    useMode: () => {
      const [m, setM] = useState<PlanetMode>(
        typeof window !== 'undefined' && window.__planetMode
          ? window.__planetMode
          : 'narrative',
      );
      useEffect(() => {
        const cb = (mode: PlanetMode) => setM(mode);
        modeListeners.add(cb);
        // Sync in case the mode changed between the render and the
        // effect.
        const cur =
          typeof window !== 'undefined' && window.__planetMode
            ? window.__planetMode
            : 'narrative';
        if (cur !== m) setM(cur);
        return () => {
          modeListeners.delete(cb);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);
      return m;
    },
  };

  // Expose the imperative handles the world needs.
  if (typeof window !== 'undefined') {
    (window as any).__planetModeController = {
      setMode: controller.setMode,
      toggle: controller.toggle,
      getMode: controller.getMode,
      // setTourActive is intentionally NOT exposed on the public
      // controller — only useTourController may flip it. The mode
      // controller is reserved for human-initiated mode changes.
    };
  }

  return controller;
}

export default useModeController;
